import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Receipt, Plus, Trash2, Search, Loader2, RefreshCw, ExternalLink, ChevronLeft, ChevronRight, Copy, Settings2 } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import BillingWizardModal from "../components/BillingWizardModal";
import BillingDetailModal from "../components/BillingDetailModal";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const STATUS_MAP = {
  PENDING: { label: "Pendente", bg: "bg-amber-100 text-amber-700" },
  RECEIVED: { label: "Pago", bg: "bg-emerald-100 text-emerald-700" },
  CONFIRMED: { label: "Confirmado", bg: "bg-emerald-100 text-emerald-700" },
  RECEIVED_IN_CASH: { label: "Pago em dinheiro", bg: "bg-emerald-100 text-emerald-700" },
  OVERDUE: { label: "Vencido", bg: "bg-rose-100 text-rose-700" },
  REFUNDED: { label: "Estornado", bg: "bg-slate-200 text-slate-600" },
  CANCELLED: { label: "Cancelado", bg: "bg-slate-200 text-slate-600" },
  DELETED: { label: "Excluído", bg: "bg-slate-200 text-slate-600" },
};

const TYPE_LABELS = { BOLETO: "Boleto", PIX: "Pix", CREDIT_CARD: "Cartão", UNDEFINED: "—" };

export default function CobrancasPage() {
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const tc50 = appearance.colors?.["50"] || "#EFF6FF";
  const [billings, setBillings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const COLUMNS = [
    { id: "negocio", label: "Negócio" },
    { id: "description", label: "Descrição" },
    { id: "tipo", label: "Tipo" },
    { id: "valor", label: "Valor" },
    { id: "vencimento", label: "Vencimento" },
    { id: "status", label: "Status" },
    { id: "created_at", label: "Criado em" },
  ];
  const [colVisibility, setColVisibility] = useState(() => Object.fromEntries(COLUMNS.map(c => [c.id, true])));
  const isColVisible = (id) => colVisibility[id] !== false;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: String(page * PAGE_SIZE), limit: String(PAGE_SIZE) });
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      const r = await axios.get(`${API}/asaas/billings?${params}`);
      setBillings(r.data.data || []);
      setTotal(r.data.total || 0);
    } catch { setBillings([]); }
    setLoading(false);
  }, [page, search, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    axios.post(`${API}/asaas/sync-pending`).then(r => { if (r.data.synced > 0) fetchData(); }).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (newOpen) {
      axios.get(`${API}/asaas/negocios-with-contracts`).then(r => setNegocios(r.data || [])).catch(() => {});
      axios.get(`${API}/asaas/billing-templates`).then(r => setTemplates(r.data || [])).catch(() => {});
      axios.get(`${API}/integrations/asaas`).then(r => {
        const cfg = r.data?.config || {};
        if (cfg.billing_types?.length > 0) setEnabledTypes(cfg.billing_types);
      }).catch(() => {});
    }
  }, [newOpen]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

  return (
    <div className="space-y-4" data-testid="cobrancas-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
            <Receipt className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
              Cobranças
              <Badge className="bg-white text-slate-500 border border-slate-300 text-sm font-medium px-3 py-1">{total}</Badge>
            </h1>
            <p className="text-slate-500 mt-0.5">Gerencie cobranças emitidas via Asaas.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} placeholder="Buscar cobrança..." className="pl-9 w-full sm:w-56 h-9 text-sm" />
          </div>
          <Select value={statusFilter || "all"} onValueChange={v => { setStatusFilter(v === "all" ? "" : v); setPage(0); }}>
            <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="PENDING">Pendente</SelectItem>
              <SelectItem value="RECEIVED">Pago</SelectItem>
              <SelectItem value="OVERDUE">Vencido</SelectItem>
              <SelectItem value="CANCELLED">Cancelado</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="h-9 w-9 text-slate-600" onClick={fetchData}><RefreshCw className="w-4 h-4" /></Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9 text-slate-600"><Settings2 className="w-4 h-4" /></Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 mb-2">Colunas visíveis</p>
              {COLUMNS.map(col => (
                <label key={col.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm text-slate-700">
                  <Checkbox checked={isColVisible(col.id)} onCheckedChange={v => setColVisibility(prev => ({ ...prev, [col.id]: !!v }))} />
                  {col.label}
                </label>
              ))}
            </PopoverContent>
          </Popover>
          <Button onClick={() => setNewOpen(true)} size="icon" style={{ backgroundColor: tc500 }} className="text-white h-9 w-9 hover:opacity-90">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {(() => {
        const TH = ({ id, children, className = "text-left" }) => isColVisible(id) ? <th className={`px-3 py-2.5 ${className} text-xs font-semibold text-slate-500 uppercase tracking-wider`}>{children}</th> : null;
        const visibleCount = COLUMNS.filter(c => isColVisible(c.id)).length + 1;
        const renderHeader = () => (
          <tr className="border-b border-slate-200 bg-slate-50/80">
            <TH id="negocio">Negócio</TH>
            <TH id="description">Descrição</TH>
            <TH id="tipo">Tipo</TH>
            <TH id="valor" className="text-right">Valor</TH>
            <TH id="vencimento">Vencimento</TH>
            <TH id="status">Status</TH>
            <TH id="created_at">Criado em</TH>
            <th className="px-3 py-2.5"></th>
          </tr>
        );

        if (loading) return (
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>{renderHeader()}</thead>
                <tbody>
                  {[...Array(8)].map((_, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      {[...Array(visibleCount)].map((_, j) => (
                        <td key={j} className="px-3 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" style={{ width: `${40 + Math.random() * 40}%` }} /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

        if (billings.length === 0) return (
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <table className="w-full text-sm"><thead>{renderHeader()}</thead></table>
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Receipt className="w-10 h-10 mb-3 text-slate-300" />
              <p className="text-sm">Nenhuma cobrança encontrada.</p>
            </div>
          </div>
        );

        return (
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>{renderHeader()}</thead>
                <tbody>
                  {billings.map(b => {
                    const st = STATUS_MAP[b.status] || STATUS_MAP.PENDING;
                    return (
                      <tr key={b.id} className="border-b border-slate-100 transition-colors cursor-pointer hover:bg-slate-50/50" onClick={() => setSelected(b)}>
                        {isColVisible("negocio") && <td className="px-3 py-2.5 font-medium text-slate-800">{b.negocio_titulo || "—"}</td>}
                        {isColVisible("description") && <td className="px-3 py-2.5 text-slate-500">{b.description || "—"}</td>}
                        {isColVisible("tipo") && <td className="px-3 py-2.5 text-slate-500">{TYPE_LABELS[b.billing_type] || b.billing_type}</td>}
                        {isColVisible("valor") && <td className="px-3 py-2.5 text-right font-semibold text-slate-800">{fmt(b.value)}</td>}
                        {isColVisible("vencimento") && <td className="px-3 py-2.5 text-slate-500">{b.due_date ? new Date(b.due_date + "T12:00:00").toLocaleDateString("pt-BR") : "—"}</td>}
                        {isColVisible("status") && <td className="px-3 py-2.5"><Badge className={`${st.bg} border-0 text-[10px]`}>{st.label}</Badge></td>}
                        {isColVisible("created_at") && <td className="px-3 py-2.5 text-slate-500">{new Date(b.created_at).toLocaleDateString("pt-BR")} {new Date(b.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</td>}
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-end gap-2">
                            {b.invoice_url && <button onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(b.invoice_url); toast.success("Link copiado!"); }} className="h-8 w-8 flex items-center justify-center rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100"><Copy className="w-4 h-4" /></button>}
                            {b.invoice_url && <a href={b.invoice_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="h-8 w-8 flex items-center justify-center rounded text-slate-400 hover:text-blue-500 hover:bg-slate-100"><ExternalLink className="w-4 h-4" /></a>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">{total} cobrança(s) · Página {page + 1} de {totalPages}</p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              const pg = page < 3 ? i : page - 2 + i;
              if (pg >= totalPages) return null;
              return <Button key={pg} variant={pg === page ? "default" : "outline"} size="sm" className="h-8 w-8 p-0 text-xs" style={pg === page ? { backgroundColor: tc500 } : {}} onClick={() => setPage(pg)}>{pg + 1}</Button>;
            })}
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <BillingDetailModal
        billing={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onRefresh={fetchData}
      />

      {/* Billing Wizard */}
      <BillingWizardModal open={newOpen} onClose={() => setNewOpen(false)} onBillingSent={() => fetchData()} />
    </div>
  );
}
